import type { ThemeMeta, ThemeTokens } from '@/previewThemes/meta';

/**
 * Kinetic's design constants.
 *
 * A performance-sports house, and the one dark theme in the set: near-black
 * pages, charcoal product tiles, white type and a single blaze-orange signal colour on
 * the announcement rail, the buttons and every price drop. Headings are set in
 * a condensed grotesque, in capitals, the way a race number or a kit label is.
 *
 * The shop it is previewed with is a sports store stocked with the platform's
 * own Sports library pictures - see `catalog.ts` for why it ships its own, and
 * `photos.ts` for where each picture came from.
 */
export const tokens: ThemeTokens = {
  // Narrow, heavy and upright: a lot of word in a little width, which is what
  // lets a section title run in capitals without taking two lines.
  headingFont: 'Barlow Condensed',
  // The blaze orange. It is also the primary in `look`, so a merchant who repaints
  // their primary moves the buttons; this is the note the theme keeps for its
  // own details - the sale line, the in-stock note.
  accent: '#ff6a13',
  monoFont: null,
  headingTracking: '0.01em',
  headingTransform: 'uppercase',
  // Left-aligned: a sports shop's section titles sit over the left edge of a
  // row that runs off to the right, not centred over a showcase.
  headingAlign: 'left',
  // The full-width hero ends on a diagonal, the page's near-black rising into
  // the photograph from the left - the one angled line in the theme.
  heroEdge: 'slant',
  // Stars under every card: a sports shopper picks kit by what the people who
  // trained in it thought of it.
  cardRatings: true,
  containerMax: '84rem',
  spaceScale: 1,
  // The orange rail and one dark deck.
  headerHeight: '6.75rem',
};

export const meta: ThemeMeta = {
  label: 'Kinetic',
  hint: 'A dark performance-sports shop: charcoal tiles, orange signal colour and condensed capitals.',
  industries: ['sports'],
  look: {
    colors: {
      // Blaze orange as the primary: a solid button paints its label in the page
      // colour, so the buttons come out orange with near-black capitals.
      primary: '#ff6a13',
      background: '#141414',
      surface: '#262626',
      text: '#f5f5f5',
      muted: '#9a9a9a',
    },
    typography: {
      font_family: 'Barlow',
      heading_font: 'Barlow Condensed',
      heading_weight: '700',
    },
    // Square corners throughout: kit, not jewellery.
    buttons: { radius_px: 0, style: 'solid' },
    // No `catalog` named: this theme ships its own in `catalog.ts`.
  },
};
